import { useTranslation } from 'react-i18next';

import { Popup } from 'components/Display/Popup/Popup';
import { Button } from 'components/Form/Button/Button';

import { InvalidNesting } from '../../helpers/unitTypeHierarchy';
import { UnitTypeOption } from '../../state/packingListReducer';

type UnitNestingWarningDialogProps = {
  nestings: Array<InvalidNesting>;
  unitTypeOptions: Array<UnitTypeOption>;
  onConfirm: () => void;
  onCancel: () => void;
};

/** Unit types show their spec label ("Pallet"), falling back to the raw canonical value. */
export const UnitNestingWarningDialog = ({
  nestings,
  unitTypeOptions,
  onConfirm,
  onCancel,
}: UnitNestingWarningDialogProps) => {
  const { t } = useTranslation();
  const labelFor = (unitType: string) => unitTypeOptions.find((option) => option.value === unitType)?.label ?? unitType;

  return (
    <Popup
      open
      onClose={onCancel}
      title={t('webedi:packing_list.nesting_warning.title')}
      subtitle={t('webedi:packing_list.nesting_warning.body')}
      testId="packing-list-nesting-warning"
      CancelButton={({ cancelButtonRef }) => (
        <Button
          variant="secondary"
          ref={cancelButtonRef}
          onClick={onCancel}
          analyticsId="webedi:packing_list_nesting_keep_editing"
        >
          {t('webedi:packing_list.nesting_warning.keep_editing')}
        </Button>
      )}
      PrimaryButton={() => (
        <Button onClick={onConfirm} analyticsId="webedi:packing_list_nesting_apply_anyway">
          {t('webedi:packing_list.nesting_warning.confirm')}
        </Button>
      )}
    >
      <ul className="mt-2 space-y-1 text-sm text-gray-600">
        {nestings.map((nesting) => (
          <li key={`${nesting.parentPosition}>${nesting.childPosition}`}>
            {t('webedi:packing_list.nesting_warning.item', {
              childPosition: nesting.childPosition,
              childUnitType: labelFor(nesting.childUnitType),
              parentPosition: nesting.parentPosition,
              parentUnitType: labelFor(nesting.parentUnitType),
            })}
          </li>
        ))}
      </ul>
    </Popup>
  );
};
